import { useCallback, useEffect, useRef, useState } from "react";
import { post } from "../services/services";
import { ApiURL } from "../services/apiConstants";

const responseBody = (response) => response?.response?.data || response;

export default function useSubscriptionAccess(endpoint) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [trialError, setTrialError] = useState("");
  const mounted = useRef(false);
  const requestId = useRef(0);
  const activating = useRef(false);

  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const result = responseBody(await post(endpoint, new FormData()));
      if (result?.status !== true || typeof result?.data?.subscription?.active !== "boolean") {
        throw new Error("We could not verify your company’s access. Please try again.");
      }
      if (mounted.current && id === requestId.current) {
        setData(result.data);
        setError("");
      }
    } catch (err) {
      if (mounted.current && id === requestId.current) {
        setError("We could not verify your company’s access. Check your connection and try again.");
      }
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const onFocus = () => refresh();
    const onStorage = (event) => {
      if (event.key === "company_subscription_updated") refresh();
    };
    const onAccessRequired = (event) => {
      setData((previous) => ({ ...previous, subscription: event.detail }));
      refresh();
    };
    const interval = window.setInterval(refresh, 60000);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    window.addEventListener("subscription-required", onAccessRequired);
    return () => {
      mounted.current = false;
      requestId.current += 1;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("subscription-required", onAccessRequired);
    };
  }, [refresh]);

  const subscription = data?.subscription;
  useEffect(() => {
    if (!subscription?.active || !subscription.expires_at) return undefined;
    // Use the server's clock so a workstation with an incorrect clock cannot
    // trigger early expiry or a tight revalidation loop.
    const checkedAt = subscription.checked_at ? Date.parse(subscription.checked_at) : Date.now();
    const delay = Date.parse(subscription.expires_at) - checkedAt;
    if (!Number.isFinite(delay)) return undefined;
    const timer = window.setTimeout(() => {
      // Hide protected content while verifying the exact expiry boundary.
      setLoading(true);
      refresh();
    }, Math.max(1000, Math.min(delay, 2147483647)));
    return () => window.clearTimeout(timer);
  }, [subscription, refresh]);

  const startTrial = async () => {
    if (activating.current || !subscription?.trial?.eligible) return;
    activating.current = true;
    setStarting(true);
    setTrialError("");
    try {
      const result = responseBody(await post(ApiURL.company_start_trial, new FormData()));
      if (result?.status !== true) {
        if (mounted.current) {
          setTrialError(result?.message || "The trial could not be activated. Check your connection and try again.");
        }
      } else {
        localStorage.setItem("company_subscription_updated", String(Date.now()));
      }
      // Also reconcile conflicts and uncertain network responses (the server
      // may already have committed the activation).
      await refresh();
    } catch (err) {
      if (mounted.current) setTrialError("The trial could not be activated. Please check the access status and try again.");
      await refresh();
    } finally {
      activating.current = false;
      if (mounted.current) setStarting(false);
    }
  };

  return { data, subscription, loading, error, starting, trialError, refresh, startTrial,
    hasAccess: !loading && !error && subscription?.active === true };
}
